import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  alertStatusLegend,
  alertStatusMap,
  allowedTypeSource,
  CLOSE_NETSPACE_DRAWER,
  GET_SIDE_DRAWER_ALERTS,
  sideDrawerColumns,
} from "./NetSpaceConstant";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "../../assets/vizr_images/ic_remove.svg";
import { Button, useMediaQuery } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import {
  renderIsMonitored,
  renderHostIPFnWrapper,
  renderHostType,
  renderOS,
  renderPorts,
  renderTags,
  renderHostName,
  renderHostMacAddress,
  renderSource,
  renderCVEScore,
  renderCVESeverity,
  renderCVEId,
  renderUserTagsWithHostTag,
} from "./NetSpaceUtils";

const useStyles = makeStyles({
  paper: {
    background: "white",
  },
});

const NetSpaceDrawer = () => {
  const classes = useStyles();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const dispatch = useDispatch();
  const renderHostIP = renderHostIPFnWrapper(dispatch);

  const drawerLoader = useSelector(
    (state) => state.NetspaceReducer.drawerLoader,
  );

  const selectedHost = useSelector(
    (state) => state.NetspaceReducer.selectedHost,
  );

  const drawerIsOpen = useSelector(
    (state) => state.NetspaceReducer.drawerIsOpen,
  );

  const alertsData = useSelector(
    (state) => state.NetspaceReducer.sideDrawerAlerts,
  );

  const alerts = alertsData?.content || [];
  const totalCount = alertsData?.totalElements || 0;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [selectedAlertStatus, setSelectedAlertStatus] = useState(null);

  const handlePaginationChange = useCallback((model) => {
    setPaginationModel(model);
  }, []);

  useEffect(() => {
    if (!selectedHost?.nodeId) return;

    dispatch({
      type: GET_SIDE_DRAWER_ALERTS,
      payload: {
        hostId: selectedHost.nodeId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      },
    });
  }, [dispatch, selectedHost?.nodeId, paginationModel]);

  const rows = useMemo(() => {
    return alerts.map((item, index) => ({
      id: index + paginationModel.page * paginationModel.pageSize,
      severity: item.severity,
      status: alertStatusMap[item.status],
      alertName: item.alertName,
    }));
  }, [alerts, paginationModel]);

  if (!drawerIsOpen) return null;

  return (
    <Drawer
      classes={{ paper: classes.paper }}
      anchor="right"
      open={drawerIsOpen}
      onClose={() => dispatch({ type: CLOSE_NETSPACE_DRAWER })}
    >
      {drawerLoader ? (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source tenantProfileLoader"
        />
      ) : (
        <div className="netspace_drawer_main">
          <div className="netspace_drawer_container">
            {/* Header */}
            <div className="netspace_drawer_header">
              <div className="netspace_details">Host Details</div>
              <Button
                className="close_drawer"
                onClick={() => dispatch({ type: CLOSE_NETSPACE_DRAWER })}
              >
                <img src={CloseIcon} alt="close" />
              </Button>
            </div>

            <div className="netspace_part2">
              {/* Host Details */}
              <div className="netspace_content_DB">
                <span className="header">Host Name:</span>{" "}
                {renderHostName({ row: selectedHost })}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Host IP:</span>{" "}
                {renderHostIP({ row: selectedHost }, "fullView")}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Mac Address:</span>{" "}
                {renderHostMacAddress({ row: selectedHost })}
              </div>
              <Divider />

              <div className="netspace_content_DB filters">
                <div className="netspace-side-filter-header">
                  <span className="header">Vulnerabilities:</span>

                  <div className="cve-filter-rings">
                    <span className="filter-title">Quick Filter:</span>
                    {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((sev) => {
                      const isActive = selectedSeverity === sev;

                      return (
                        <span
                          key={sev}
                          className={`severity-ring ${sev.toLowerCase()} ${
                            isActive ? "active" : ""
                          }`}
                          title={sev}
                          onClick={() =>
                            setSelectedSeverity((prev) =>
                              prev === sev ? null : sev,
                            )
                          }
                        />
                      );
                    })}
                  </div>
                </div>

                {renderCVEId(
                  { row: selectedHost },
                  "fullView",
                  selectedSeverity,
                )}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Vulnerability Score:</span>{" "}
                {renderCVEScore({ row: selectedHost })}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Vulnerabilities Total :</span>{" "}
                {renderCVESeverity({ row: selectedHost })}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">OS:</span>{" "}
                {renderOS({ row: selectedHost }, "fullView")}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">System Tags:</span>{" "}
                {renderTags({ row: selectedHost }, "fullView")}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <div className="netspace-side-filter-header alerts">
                  <span className="header">Alerts:</span>
                  <div className="common-header-container">
                    <span className="filter-title">Quick Filters: </span>
                    <div className="common-header-container items">
                      {alertStatusLegend.map((status) => {
                        const isActive = selectedAlertStatus === status.label;

                        return (
                          <span
                            key={status.class}
                            className={`alert-status-netspace ${
                              isActive ? "active" : ""
                            }`}
                            title={status.label}
                            onClick={() => {
                              setSelectedAlertStatus((prev) =>
                                prev === status.label ? null : status.label,
                              );
                              dispatch({
                                type: GET_SIDE_DRAWER_ALERTS,
                                payload: {
                                  status: status.filter,
                                  hostId: selectedHost.nodeId,
                                  page: paginationModel.page,
                                  size: paginationModel.pageSize,
                                },
                              });
                            }}
                          >
                            {status.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="alerts-table-container">
                <div className="data-grid-wrapper">
                  <DataGrid
                    rows={rows}
                    columns={sideDrawerColumns}
                    rowCount={totalCount}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationChange}
                    pageSizeOptions={[5, 10, 20, 50]}
                    disableRowSelectionOnClick
                    loading={drawerLoader}
                    hideFooterSelectedRowCount
                    disableColumnMenu
                    getRowHeight={() => "auto"}
                  />
                </div>
              </div>

              <Divider />

              {/* Remaining Fields */}
              <div className="netspace_content_DB">
                <span className="header">Agents:</span>{" "}
                {renderSource({ row: selectedHost }, "fullView")}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Visibility:</span>{" "}
                {renderIsMonitored({ row: selectedHost })}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">
                  {allowedTypeSource.includes(selectedHost?.sourceType)
                    ? "Type:"
                    : "Host Type:"}
                </span>{" "}
                {renderHostType({ row: selectedHost }, "fullView")}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">User Tags:</span>{" "}
                {renderUserTagsWithHostTag(
                  { row: selectedHost },
                  null,
                  dispatch,
                  "fullView",
                )}
              </div>
              <Divider />

              <div className="netspace_content_DB">
                <span className="header">Ports:</span>{" "}
                {renderPorts({ row: selectedHost }, "fullView", isSmall)}
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default NetSpaceDrawer;
